/**
 * Loop di riproduzione. Nessun DOM: dipende da interfacce iniettate (orologio, scheduler, superfici,
 * provider degli asset), così è testabile con finti.
 *
 * Modello: ogni contenuto in onda ha un "piano" per il successivo:
 *   plan = { at, trigger, item }
 *     - trigger 'end'  (libera, contenuti con durata): il successivo si valuta con nextItem a t = fine prevista;
 *       il cambio avviene alla fine del video (evento ended, con watchdog) o dopo duration_ms (immagini).
 *     - trigger 'time' (sync, e schermo nero/fallback fermo): il cambio avviene a un istante calcolato
 *       (fine elemento o cambio di segmento in sync; prossimo cambio dell'insieme attivo altrimenti).
 *   Il successivo è PRECARICATO nello slot nascosto appena il corrente parte; al momento del cambio si
 *   ricalcola e, se il risultato è diverso da quanto preparato (manifest/asset cambiati), si prepara di nuovo.
 *
 * Gli asset non in cache vengono tolti dal manifest passato al motore: le voci che li usano si saltano.
 */
import {
  nextActiveSetChange, nextItem, timelineAt,
} from '../engine/schedule-engine';
import type { EventSink } from '../diag';
import { NULL_SINK } from '../diag';
import type {
  ContentItem, CurrentRef, FallbackItem, Manifest, ManifestAsset, TimelineItem,
} from '../types';
import { driftCorrection, SEEK_THRESHOLD_MS } from './drift';
import { SupersededError, type AssetProvider, type Scheduler, type Surfaces } from './types';

export const IDLE_RECHECK_MS = 60_000;
export const FAIL_COOLDOWN_MS = 60_000;
export const VIDEO_WATCHDOG_MS = 3_000;
export const DRIFT_TICK_MS = 500;
const INITIAL_SEEK_MS = 120;

export interface ControllerDeps {
  surfaces: Surfaces;
  assets: AssetProvider;
  clock: { now(): number };
  scheduler: Scheduler;
  events?: EventSink;
  perfNow?: () => number;
  initialCommitLatencyMs?: number;
  log?: (msg: string) => void;
  /** Chiamato PRIMA che il contenuto corrente venga lasciato: `completed` = ha girato fino alla sua fine. */
  onLeaving?: (completed: boolean) => void;
}

interface Resolved {
  fallback: boolean;
  scheduleId: string | null;
  playIndex: number;
  asset: ManifestAsset;
  /** null = a tempo indefinito (immagine di fallback). */
  durationMs: number | null;
  offsetMs: number;
  /** Istante a cui si riferisce `offsetMs` (sync). */
  resolvedAt: number;
  key: string;
}

interface Playing extends Resolved {
  startedAt: number;
}

interface Plan {
  at: number;
  trigger: 'end' | 'time';
  item: Resolved | null;
  /** false se il cambio previsto taglia il contenuto prima della sua fine (cambio di segmento in sync). */
  completed: boolean;
}

interface Prepared {
  key: string;
  promise: Promise<boolean>;
}

export type Phase = 'idle' | 'playing' | 'fallback' | 'black';

export interface ControllerState {
  phase: Phase;
  mode: 'free' | 'sync';
  manifestVersion: string | null;
  current: { scheduleId: string | null; assetId: string; kind: string; playIndex: number; fallback: boolean; slot: string | null } | null;
  nextKey: string | null;
  planAt: number | null;
  failedAssets: string[];
  commitLatencyMs: number;
}

export class PlaybackController {
  private manifest: Manifest | null = null;
  private eff: Manifest | null = null;
  private current: Playing | null = null;
  private lastRef: CurrentRef | null = null;
  private plan: Plan | null = null;
  private prepared: Prepared | null = null;
  private timer: unknown = null;
  private driftTimer: unknown = null;
  private gen = 0;
  private started = false;
  private transitioning = false;
  private interrupted = false; // errore del video corrente: il prossimo cambio non è un completamento
  private rate = 1;
  private commitLatency: number;
  private readonly failed = new Map<string, number>();
  private readonly events: EventSink;
  private readonly perfNow: () => number;

  constructor(private readonly d: ControllerDeps) {
    this.events = d.events ?? NULL_SINK;
    this.perfNow = d.perfNow ?? (() => (typeof performance !== 'undefined' ? performance.now() : Date.now()));
    this.commitLatency = d.initialCommitLatencyMs ?? 60;
    d.surfaces.onEnded(() => this.onVideoEnded());
    d.surfaces.onError((msg) => this.onVideoError(msg));
  }

  // --- API pubblica -------------------------------------------------------

  start(): void {
    this.started = true;
    this.reevaluate();
  }

  /** Ferma tutto e mostra nero (schermo disattivato, abbonamento non attivo, ritorno al pairing). */
  stop(): void {
    this.started = false;
    this.gen++;
    this.transitioning = false;
    this.clearTimers();
    this.d.surfaces.discardPrepared();
    if (this.current) this.d.onLeaving?.(false);
    this.d.surfaces.showBlack();
    this.current = null;
    this.prepared = null;
    this.plan = null;
  }

  /** Nuovo manifest (già filtrato dall'adozione): si applica senza interrompere il contenuto in corso. */
  setManifest(m: Manifest | null): void {
    this.manifest = m;
    this.eff = null;
    this.onInputsChanged();
  }

  /** La disponibilità degli asset è cambiata (download finito, GC, clear_cache). */
  assetsChanged(): void {
    this.eff = null;
    this.onInputsChanged();
  }

  state(): ControllerState {
    const cur = this.current;
    const m = this.manifest;
    return {
      phase: !this.started ? 'idle' : !cur ? 'black' : cur.fallback ? 'fallback' : 'playing',
      mode: m?.sync_group ? 'sync' : 'free',
      manifestVersion: m?.version ?? null,
      current: cur ? {
        scheduleId: cur.scheduleId, assetId: cur.asset.id, kind: cur.asset.kind, playIndex: cur.playIndex,
        fallback: cur.fallback, slot: this.d.surfaces.visibleSlot(),
      } : null,
      nextKey: this.plan?.item?.key ?? null,
      planAt: this.plan?.at ?? null,
      failedAssets: [...this.failed.keys()],
      commitLatencyMs: Math.round(this.commitLatency),
    };
  }

  // --- motore -------------------------------------------------------------

  private effective(): Manifest | null {
    if (!this.manifest) return null;
    if (!this.eff) {
      const now = this.d.clock.now();
      for (const [id, until] of this.failed) if (until <= now) this.failed.delete(id);
      this.eff = {
        ...this.manifest,
        assets: this.manifest.assets.filter((a) => this.d.assets.isReady(a.id, a.sha256) && !this.failed.has(a.id)),
      };
    }
    return this.eff;
  }

  private resolve(t: number): Resolved | null {
    const m = this.effective();
    if (!m) return null;
    try {
      const r = m.sync_group ? timelineAt(m, t) : nextItem(m, t, this.lastRef);
      if (r === null) return null;
      if ('fallback' in r) return this.fromFallback(m, r, t);
      const asset = m.assets.find((a) => a.id === r.asset_id) as ManifestAsset;
      return {
        fallback: false,
        scheduleId: r.schedule_id,
        playIndex: r.play_index,
        asset,
        durationMs: r.duration_ms,
        // solo timelineAt (modalità sync) restituisce offset_ms
        offsetMs: isTimelineItem(r) ? r.offset_ms : 0,
        resolvedAt: t,
        key: `${r.schedule_id}#${r.play_index}@${r.asset_id}`,
      };
    } catch (e) {
      this.d.log?.(`motore: ${e instanceof Error ? e.message : String(e)}`);
      return null;
    }
  }

  private fromFallback(m: Manifest, f: FallbackItem, t: number): Resolved {
    const asset = m.assets.find((a) => a.id === f.asset_id) as ManifestAsset;
    return {
      fallback: true,
      scheduleId: null,
      playIndex: 0,
      asset,
      durationMs: asset.kind === 'video' ? (asset.duration_ms ?? null) : null,
      offsetMs: 0,
      resolvedAt: t,
      key: `fallback@${f.asset_id}`,
    };
  }

  // --- reazioni agli ingressi --------------------------------------------

  private onInputsChanged(): void {
    if (!this.started || this.transitioning) return; // a fine transizione planNext usa gli ingressi aggiornati
    const cur = this.current;
    if (!cur || cur.durationMs === null) {
      this.reevaluate(); // nero o fallback fermo: si può cambiare subito
    } else {
      this.planNext(); // contenuto in corso: non lo si interrompe, ma si aggiorna il precaricamento
    }
  }

  private reevaluate(): void {
    if (!this.started || this.transitioning) return;
    this.clearTimers();
    const r = this.resolve(this.d.clock.now());
    const cur = this.current;
    if (cur && cur.durationMs === null && r && r.key === cur.key) { this.planNext(); return; }
    if (!cur && !r) { this.planNext(); return; }
    if (cur) this.d.onLeaving?.(cur.durationMs === null); // contenuto lasciato fuori piano: interrotto (tranne il fallback fermo)
    void this.transitionTo(r);
  }

  private onVideoEnded(): void {
    const cur = this.current;
    if (!this.started || !cur || this.transitioning || cur.asset.kind !== 'video') return;
    if (this.plan?.trigger !== 'end') return; // in sync decide l'orologio, non l'evento
    void this.boundary();
  }

  private onVideoError(msg: string): void {
    const cur = this.current;
    this.d.log?.(`errore video: ${msg}`);
    if (!cur) return;
    this.markFailed(cur.asset.id);
    this.interrupted = true;
    if (this.transitioning) return;
    if (this.plan?.trigger === 'end') void this.boundary();
    else this.reevaluate();
  }

  private markFailed(assetId: string): void {
    this.failed.set(assetId, this.d.clock.now() + FAIL_COOLDOWN_MS);
    this.eff = null;
  }

  // --- piano e timer -----------------------------------------------------

  private clearTimers(): void {
    if (this.timer !== null) { this.d.scheduler.clearTimeout(this.timer); this.timer = null; }
    if (this.driftTimer !== null) { this.d.scheduler.clearTimeout(this.driftTimer); this.driftTimer = null; }
  }

  private idleCheckTime(now: number): number {
    const m = this.effective();
    const change = m ? nextActiveSetChange(m, now) : null;
    return Math.min(change ?? Infinity, now + IDLE_RECHECK_MS);
  }

  /** Calcola il piano per il dopo, precarica, arma i timer. Idempotente. */
  private planNext(): void {
    if (this.timer !== null) { this.d.scheduler.clearTimeout(this.timer); this.timer = null; }
    const cur = this.current;
    const now = this.d.clock.now();
    const m = this.effective();
    let at: number;
    let trigger: 'end' | 'time';
    let completed = true;
    if (cur && m?.sync_group && !cur.fallback) {
      const elemEnd = cur.resolvedAt + ((cur.durationMs as number) - cur.offsetMs);
      const seg = nextActiveSetChange(m, cur.resolvedAt);
      at = Math.min(elemEnd, seg ?? Infinity);
      completed = at >= elemEnd;
      trigger = 'time';
    } else if (cur && cur.durationMs !== null) {
      at = cur.startedAt + cur.durationMs;
      trigger = 'end';
    } else {
      at = this.idleCheckTime(now);
      trigger = 'time';
    }
    const item = this.resolve(at);
    this.plan = { at, trigger, item, completed };

    // precarica il successivo nello slot nascosto
    const needsPrepare = item !== null && !(cur && cur.durationMs === null && item.key === cur.key);
    if (needsPrepare && item) {
      if (this.prepared?.key !== item.key) {
        this.d.surfaces.discardPrepared();
        this.prepared = this.startPrepare(item);
      }
    } else if (this.prepared) {
      this.d.surfaces.discardPrepared();
      this.prepared = null;
    }

    // timer del cambio
    const isVideoEnd = trigger === 'end' && cur?.asset.kind === 'video';
    const delay = isVideoEnd
      ? at + VIDEO_WATCHDOG_MS - now
      : at - this.commitLatency - now;
    this.timer = this.d.scheduler.setTimeout(() => { this.timer = null; void this.boundary(); }, Math.max(0, delay));

    this.armDrift();
  }

  private armDrift(): void {
    if (this.driftTimer !== null) { this.d.scheduler.clearTimeout(this.driftTimer); this.driftTimer = null; }
    const cur = this.current;
    if (!cur || cur.fallback || cur.asset.kind !== 'video' || !this.effective()?.sync_group) return;
    this.driftTimer = this.d.scheduler.setTimeout(() => {
      this.driftTimer = null;
      this.driftTick();
      this.armDrift();
    }, DRIFT_TICK_MS);
  }

  /** `seekAtMs`: soglia di seek; subito dopo l'avvio è più bassa (il caricamento ha rubato tempo). */
  private driftTick(seekAtMs = SEEK_THRESHOLD_MS, force = false): void {
    const cur = this.current;
    if (!cur || (this.transitioning && !force)) return;
    const pos = this.d.surfaces.positionMs();
    if (pos === null) return;
    const expected = cur.offsetMs + (this.d.clock.now() - cur.resolvedAt);
    if (expected < 0 || expected >= (cur.durationMs as number)) return; // a cavallo del cambio: ci pensa il boundary
    const c = driftCorrection(pos - expected, this.rate, seekAtMs);
    if (c.action === 'seek') {
      this.d.surfaces.seekMs(expected);
      this.rate = 1;
      this.d.surfaces.setRate(1);
    } else if (c.action === 'rate') {
      this.rate = c.rate;
      this.d.surfaces.setRate(c.rate);
    }
  }

  private startPrepare(item: Resolved): Prepared {
    const promise = (async (): Promise<boolean> => {
      try {
        const url = await this.d.assets.getUrl(item.asset.id, item.asset.sha256);
        await this.d.surfaces.prepare({
          assetId: item.asset.id, scheduleId: item.scheduleId, kind: item.asset.kind, url, offsetMs: item.offsetMs, durationMs: item.durationMs,
        });
        return true;
      } catch (e) {
        if (e instanceof SupersededError) return false; // scartata da noi: l'asset non ha colpe
        this.d.log?.(`preparazione ${item.asset.id} fallita: ${e instanceof Error ? e.message : String(e)}`);
        this.markFailed(item.asset.id);
        return false;
      }
    })();
    return { key: item.key, promise };
  }

  // --- cambio di contenuto -----------------------------------------------

  /** Fine del contenuto corrente (o istante pianificato): valuta e cambia. */
  private async boundary(): Promise<void> {
    if (!this.started || this.transitioning) return;
    if (this.timer !== null) { this.d.scheduler.clearTimeout(this.timer); this.timer = null; }
    const plan = this.plan;
    const cur = this.current;
    const now = this.d.clock.now();
    let tRef: number;
    if (!plan) tRef = now;
    else if (plan.trigger === 'end') tRef = Math.max(plan.at, now);
    else tRef = now - plan.at > 1000 ? now : plan.at; // in ritardo di oltre 1 s: valuta a tempo reale

    if (cur && cur.durationMs !== null) this.events.push('ended', this.d.surfaces.visibleSlot(), cur.scheduleId);

    const r = this.resolve(tRef);
    if (cur && cur.durationMs === null && r && r.key === cur.key) { this.planNext(); return; }
    if (!cur && !r) { this.planNext(); return; }
    if (cur) this.d.onLeaving?.((plan?.completed ?? true) && !this.interrupted);
    this.interrupted = false;
    await this.transitionTo(r);
  }

  private async transitionTo(r: Resolved | null): Promise<void> {
    const g = ++this.gen;
    this.transitioning = true;
    this.clearTimers();
    try {
      if (r === null) {
        this.d.surfaces.discardPrepared();
        this.prepared = null;
        this.d.surfaces.showBlack();
        this.current = null;
        return;
      }
      let p = this.prepared;
      if (!p || p.key !== r.key) {
        this.d.surfaces.discardPrepared();
        p = this.startPrepare(r);
      }
      this.prepared = null;
      const ok = await p.promise;
      if (g !== this.gen) return;
      if (!ok) {
        this.retryLater();
        return;
      }
      const t0 = this.perfNow();
      try {
        await this.d.surfaces.commit();
      } catch (e) {
        if (g !== this.gen) return;
        this.d.log?.(`commit ${r.asset.id} fallito: ${e instanceof Error ? e.message : String(e)}`);
        this.markFailed(r.asset.id);
        this.retryLater();
        return;
      }
      if (g !== this.gen) return;
      const measured = Math.max(0, this.perfNow() - t0);
      this.commitLatency = Math.min(300, 0.7 * this.commitLatency + 0.3 * measured);
      this.rate = 1;
      this.current = { ...r, startedAt: this.d.clock.now() };
      if (!r.fallback && r.scheduleId !== null) this.lastRef = { schedule_id: r.scheduleId, play_index: r.playIndex };
      if (this.manifest?.sync_group && !r.fallback && r.asset.kind === 'video') this.driftTick(INITIAL_SEEK_MS, true);
    } finally {
      if (g === this.gen) {
        this.transitioning = false;
        if (this.started && this.timer === null) this.planNext();
      }
    }
  }

  /** Dopo un errore: riprova a rivalutare tra poco (l'asset guasto è escluso per un minuto). */
  private retryLater(): void {
    this.transitioning = false;
    this.timer = this.d.scheduler.setTimeout(() => { this.timer = null; this.reevaluate(); }, 300);
  }
}

// solo timelineAt (modalità sync) restituisce offset_ms
function isTimelineItem(r: ContentItem | TimelineItem): r is TimelineItem {
  return typeof (r as Partial<TimelineItem>).offset_ms === 'number';
}
